// JSON rendering for agent replies — extracted from AgentDetail.tsx
// (was ~320 lines inline). Pure leaf functions + components with no
// dependency on AgentDetail's state, so they live cleanly on their own.
//
// Exports consumed by AgentDetail's SectionContent / TurnEntry:
//   tryParseJson, PrettyJson, SemanticAgentCard, hasSemanticFields,
//   splitTextAndJson.

import type { ReactNode } from 'react';
import { serviceOrigin } from '../lib/services';

// Escape control chars (newlines / tabs / carriage returns) that appear
// raw INSIDE string values. LLM-emitted JSON routinely violates RFC 8259
// by including literal 0x0A bytes in string contents (e.g. multi-line
// `content` fields written without escape). Strict JSON.parse rejects
// these — we walk the candidate, track string state, and rewrite raw
// control chars to their JSON-escape form before re-parsing.
function escapeRawControlsInStrings(text: string): string {
  let out = '';
  let inString = false;
  let escape = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (escape) { out += ch; escape = false; continue; }
    if (ch === '\\') { out += ch; escape = true; continue; }
    if (ch === '"') { inString = !inString; out += ch; continue; }
    if (inString) {
      if (ch === '\n') { out += '\\n'; continue; }
      if (ch === '\r') { out += '\\r'; continue; }
      if (ch === '\t') { out += '\\t'; continue; }
      // Generic control char (0x00-0x1F) → unicode escape so we don't
      // lose info if the LLM smuggled something weirder.
      const code = ch.charCodeAt(0);
      if (code < 0x20) {
        out += '\\u' + code.toString(16).padStart(4, '0');
        continue;
      }
    }
    out += ch;
  }
  return out;
}

export function tryParseJson(text: string): object | null {
  const trimmed = text.trim();
  if (!(trimmed.startsWith('{') && trimmed.endsWith('}')) && !(trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    return null;
  }
  // Strict first — most well-formed payloads succeed here.
  try { return JSON.parse(trimmed); } catch { /* lenient fallback */ }
  // Lenient: rewrite raw control chars inside string values and retry.
  // Covers the LLM-emits-multi-line-content case (GuideTest screenshot).
  try { return JSON.parse(escapeRawControlsInStrings(trimmed)); } catch { return null; }
}

// Heuristic: key names that semantically carry an EUR amount. Numeric
// values for these get rendered as `€ 558,20`, monospace + bold, so
// totals/subtotals visually pop in dense invoice-shape payloads.
// Conservative on purpose — false positives turn unrelated numbers into
// currency. Add to this set when Alex flags a key that should be money.
const CURRENCY_KEYS = /^(total|netto|brutto|summe|gesamt|betrag|preis|kosten|saldo|umsatz|honorar|geb(uehr|ühr)|mwst|ust|ddv|rabatt|skonto|gewinn|verlust|cashflow|spend|revenue)$|_eur$|_euro$|_betrag$|preis_|kosten_|brutto_|netto_/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const CURRENCY_FMT = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const DATE_FMT = new Intl.DateTimeFormat('de-DE', { dateStyle: 'long' });
const DATETIME_FMT = new Intl.DateTimeFormat('de-DE', { dateStyle: 'long', timeStyle: 'short' });

function formatJsonValue(key: string, val: unknown): ReactNode {
  // Currency by key heuristic — only triggers on plain numbers, never
  // on number-shaped strings (those usually carry a unit suffix).
  if (typeof val === 'number' && CURRENCY_KEYS.test(key)) {
    const cls = val < 0 ? 'json-value json-value-money is-negative' : 'json-value json-value-money';
    return <span className={cls}>{CURRENCY_FMT.format(val)}</span>;
  }
  if (typeof val === 'number') {
    const cls = val < 0 ? 'json-value json-value-num is-negative' : 'json-value json-value-num';
    return <span className={cls}>{val.toString()}</span>;
  }
  if (typeof val === 'string') {
    if (ISO_DATE_RE.test(val)) {
      const d = new Date(val + 'T00:00:00');
      if (!Number.isNaN(d.getTime())) {
        return <span className="json-value json-value-date" title={val}>{DATE_FMT.format(d)}</span>;
      }
    }
    if (ISO_DATETIME_RE.test(val)) {
      const d = new Date(val);
      if (!Number.isNaN(d.getTime())) {
        return <span className="json-value json-value-date" title={val}>{DATETIME_FMT.format(d)}</span>;
      }
    }
    if (val.length > 80) {
      return <span className="json-value json-value-long">{val}</span>;
    }
    return <span className="json-value">{val}</span>;
  }
  if (typeof val === 'boolean') {
    return <span className={`json-value json-value-bool ${val ? 'is-true' : 'is-false'}`}>{val ? '✓ ja' : '✗ nein'}</span>;
  }
  if (val === null) {
    return <span className="json-value json-value-null">—</span>;
  }
  return <span className="json-value">{String(val)}</span>;
}

export function PrettyJson({ data }: { data: object }) {
  if (typeof data !== 'object' || data === null) {
    return <pre className="code-block">{JSON.stringify(data, null, 2)}</pre>;
  }

  const isArray = Array.isArray(data);

  // Single-item array of an object: drop the "0:" wrapper, render the
  // single item directly under the parent key. Avoids the noise when a
  // schema technically allows a list but typically has exactly one entry
  // (Alex's invoice screenshots had `positionen: [{...}]` with only one
  // position visible as "0:" which read like a debug-dump).
  if (isArray && data.length === 1 && typeof data[0] === 'object' && data[0] !== null) {
    return <PrettyJson data={data[0] as object} />;
  }

  // Arrays render with 1-indexed item numbers (humans don't think in 0,
  // and "0:" wrapper looked like a debug index). Objects keep their key.
  const entries = isArray
    ? (data as unknown[]).map((v, i) => [String(i + 1), v] as [string, unknown])
    : Object.entries(data);

  return (
    <div className={`json-pretty${isArray ? ' json-array' : ''}`}>
      {entries.map(([key, val]) => {
        // Skip empty {} and [] — pure visual noise in agent payloads
        // where a schema has lots of optional fields. The 0-entry tells
        // the user nothing they didn't already infer from the key being
        // missing entirely.
        if (typeof val === 'object' && val !== null) {
          if (Array.isArray(val) && val.length === 0) return null;
          if (!Array.isArray(val) && Object.keys(val).length === 0) return null;
        }
        const keyClass = isArray
          ? 'json-key json-key-index'
          : key.startsWith('_')
            ? 'json-key json-key-meta'
            : 'json-key';
        return (
          <div key={key} className="json-field">
            <span className={keyClass}>{key}</span>
            {typeof val === 'object' && val !== null
              ? <PrettyJson data={val as object} />
              : formatJsonValue(key, val)}
          </div>
        );
      })}
    </div>
  );
}

// Top-level fields the persona-JSON contract carries (Cloud's IACP 57850f3a).
// Bots with native_json_format=true or pool-persona schemas emit these
// at the root of their reply payload. Anything not in this list falls
// through to PrettyJson so unknown schemas still render cleanly.
const SEMANTIC_FIELDS = ['tts_summary', 'ergebnis', 'empfehlungen', 'memory_notes', '_apus_chunk_'] as const;

export function hasSemanticFields(data: unknown): boolean {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  const obj = data as Record<string, unknown>;
  return SEMANTIC_FIELDS.some((k) => k in obj);
}

// Renderer for the chunk_committed-wrapper that content-api pushes to
// agents as a feedback signal — the agent often re-emits it inside its
// own JSON reply. Shown as a small embed-card with a deeplink instead
// of a raw JSON dump.
function ApusChunkEmbed({ chunk }: { chunk: Record<string, unknown> }) {
  const postId = typeof chunk.post_id === 'number' ? chunk.post_id : null;
  const text = typeof chunk.text === 'string' ? chunk.text : '';
  const sectionType = typeof chunk.section_type === 'string' ? chunk.section_type : null;
  const trigger = typeof chunk.trigger === 'string' ? chunk.trigger : null;
  const author = typeof chunk.author_id === 'string' ? chunk.author_id : null;
  return (
    <div className="agent-card-section agent-card-apus">
      <div className="agent-card-h">Content-API Chunk</div>
      <div className="agent-card-apus-meta">
        {postId !== null && (
          <a
            href={`${serviceOrigin('content_web') || 'https://content.arkturian.com'}/post/${postId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Post #{postId}
          </a>
        )}
        {sectionType && <span> · {sectionType}</span>}
        {trigger && <span> · {trigger}</span>}
        {author && <span> · {author.replace(/^agent:/, '')}</span>}
      </div>
      {text && (
        <div className="agent-card-apus-text">
          {text.length > 500 ? text.slice(0, 500) + '…' : text}
        </div>
      )}
    </div>
  );
}

// Render an agent-persona JSON payload as a card with semantic
// sub-blocks instead of a raw key-value dump. Unknown / unhandled
// fields collapse into a "Raw fields" details so back-compat with
// custom persona schemas stays safe.
export function SemanticAgentCard({ data }: { data: Record<string, unknown> }) {
  const tts = typeof data.tts_summary === 'string' ? data.tts_summary.trim() : '';
  const ergebnis = Array.isArray(data.ergebnis) ? data.ergebnis : null;
  const empfehlungen = Array.isArray(data.empfehlungen) ? data.empfehlungen : null;
  const memoryNotes = Array.isArray(data.memory_notes) ? data.memory_notes : null;
  const apusChunk = (data._apus_chunk_ && typeof data._apus_chunk_ === 'object' && !Array.isArray(data._apus_chunk_))
    ? data._apus_chunk_ as Record<string, unknown>
    : null;

  // Hide handled keys from the leftover dict so PrettyJson doesn't
  // double-render them. `_apus_chunk_` is handled by ApusChunkEmbed.
  const HANDLED = new Set<string>([
    'tts_summary', 'ergebnis', 'empfehlungen', 'memory_notes', '_apus_chunk_',
  ]);
  const remaining: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if (HANDLED.has(k)) continue;
    // Suppress empty arrays / objects + status-noise that's already
    // visible elsewhere in the UI (badge in header).
    if (v == null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === 'object' && Object.keys(v as object).length === 0) continue;
    remaining[k] = v;
  }
  const hasRemaining = Object.keys(remaining).length > 0;

  const renderItem = (item: unknown, i: number) => {
    if (typeof item === 'string') return <li key={i}>{item}</li>;
    if (typeof item === 'number' || typeof item === 'boolean') return <li key={i}>{String(item)}</li>;
    if (item && typeof item === 'object') {
      return <li key={i}><PrettyJson data={item as object} /></li>;
    }
    return null;
  };

  return (
    <div className="agent-card">
      {tts && (
        <div className="agent-card-summary">{tts}</div>
      )}
      {ergebnis && ergebnis.length > 0 && (
        <div className="agent-card-section">
          <div className="agent-card-h">Ergebnis</div>
          <ul className="agent-card-list">{ergebnis.map(renderItem)}</ul>
        </div>
      )}
      {empfehlungen && empfehlungen.length > 0 && (
        <div className="agent-card-section">
          <div className="agent-card-h">Empfehlungen</div>
          <ul className="agent-card-list">{empfehlungen.map(renderItem)}</ul>
        </div>
      )}
      {memoryNotes && memoryNotes.length > 0 && (
        <div className="agent-card-section agent-card-memory">
          <div className="agent-card-h">Memory Notes</div>
          <ul className="agent-card-list">{memoryNotes.map(renderItem)}</ul>
        </div>
      )}
      {apusChunk && <ApusChunkEmbed chunk={apusChunk} />}
      {hasRemaining && (
        <details className="agent-card-debug">
          <summary>Raw fields ({Object.keys(remaining).length})</summary>
          <PrettyJson data={remaining} />
        </details>
      )}
    </div>
  );
}

function findJsonInText(text: string): { start: number; end: number; data: object } | null {
  // Find the first { that starts a valid JSON object by counting braces
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '{') {
      // Skip a `{` that opens INSIDE a quoted string — e.g. a shell command
      // `printf %s '{"from":…}' | curl …` (Codex IACP-via-curl, #4231 / 113620).
      // That JSON is a command ARGUMENT, not a standalone agent-emitted block,
      // so carding it would extract the payload and leak the shell wrapper as
      // prose. A genuine standalone JSON opens at start or after whitespace.
      const prev = i > 0 ? text[i - 1] : '';
      if (prev === "'" || prev === '`') continue;
      let depth = 0;
      let inString = false;
      let escape = false;
      for (let j = i; j < text.length; j++) {
        const ch = text[j];
        if (escape) { escape = false; continue; }
        if (ch === '\\') { escape = true; continue; }
        if (ch === '"') { inString = !inString; continue; }
        if (inString) continue;
        if (ch === '{') depth++;
        if (ch === '}') { depth--; if (depth === 0) {
          const candidate = text.slice(i, j + 1);
          const parsed = tryParseJson(candidate);
          if (parsed) return { start: i, end: j + 1, data: parsed };
          break;
        }}
      }
    }
  }
  return null;
}

export function splitTextAndJson(text: string): Array<{ type: 'text' | 'json'; content: string; data?: object }> {
  // Iteratively extract EVERY JSON block in the text — agents commonly
  // emit multiple JSON payloads in one turn separated by prose or
  // Markdown headings (Alex's GuideTest case: three persona-response
  // JSONs interleaved with `#3214 — …` section headers). The previous
  // single-shot version stopped after the first JSON and rendered the
  // rest as plain text, leaving the trailing JSONs as raw curly soup.
  const parts: Array<{ type: 'text' | 'json'; content: string; data?: object }> = [];
  let cursor = 0;
  // Safety cap: if for any reason `findJsonInText` returns overlapping
  // matches or zero-length spans, bail out after a reasonable count
  // rather than spinning forever. 32 JSON blocks per turn is already
  // pathological — happens in practice never.
  for (let iter = 0; iter < 32; iter++) {
    const remaining = text.slice(cursor);
    const found = findJsonInText(remaining);
    if (!found) break;
    const before = remaining.slice(0, found.start);
    const trimmedBefore = before.trim();
    if (trimmedBefore) parts.push({ type: 'text', content: trimmedBefore });
    parts.push({
      type: 'json',
      content: remaining.slice(found.start, found.end),
      data: found.data,
    });
    // Advance past this JSON. found.end is exclusive-end index in
    // `remaining`; defensive guard: ensure forward progress so a zero-
    // length match can't loop.
    const advance = found.end > found.start ? found.end : found.start + 1;
    cursor += advance;
  }
  const tail = text.slice(cursor).trim();
  if (tail) parts.push({ type: 'text', content: tail });
  if (parts.length === 0) return [{ type: 'text', content: text }];
  return parts;
}
